import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dna, Sparkles, TrendingUp, AlertTriangle, Target, Award,
  User, Brain, Heart, Briefcase, BookOpen, Zap, ChevronRight,
  RefreshCw, CheckCircle2, XCircle
} from "lucide-react";

const SkillDNA = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dnaProfile, setDnaProfile] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState("helix");

  useEffect(() => {
    if (token) {
      fetchDNAProfile();
    }
  }, [token]);

  const fetchDNAProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/skill-dna/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDnaProfile(response.data.profile);
    } catch (error) {
      if (error.response?.status === 404) {
        // Profile doesn't exist, generate it
        await generateDNAProfile();
      } else {
        toast.error("Failed to load DNA Profile");
      }
    } finally {
      setLoading(false);
    }
  };

  const generateDNAProfile = async () => {
    try {
      setGenerating(true);
      const response = await axios.post(`${API}/skill-dna/generate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDnaProfile(response.data.profile);
      toast.success("DNA Profile generated successfully! 🧬");
    } catch (error) {
      toast.error("Failed to generate DNA Profile");
    } finally {
      setGenerating(false);
    }
  };

  const getStrandColor = (category) => {
    const colors = {
      technical: "#3B82F6",
      soft_skill: "#10B981",
      cognitive: "#8B5CF6",
      personality: "#F59E0B",
      learning: "#EC4899",
      work_style: "#14B8A6"
    };
    return colors[category] || "#6B7280";
  };

  const getScoreColor = (score) => {
    if (score >= 75) return "text-green-600 bg-green-100";
    if (score >= 50) return "text-yellow-600 bg-yellow-100";
    return "text-red-600 bg-red-100";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <Dna className="w-16 h-16 mx-auto text-indigo-500 animate-spin" />
            <p className="mt-4 text-gray-600">Analyzing your Skill DNA...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!dnaProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
        <Navbar />
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <Dna className="w-24 h-24 mx-auto text-indigo-500 mb-6" />
            <h1 className="text-4xl font-bold mb-4">Your Skill DNA Profile</h1>
            <p className="text-gray-600 mb-8">
              Discover your unique skill signature with our revolutionary DNA-style profiling.
              We analyze your technical skills, cognitive abilities, personality traits, and learning patterns
              to create a comprehensive genetic map of your career potential.
            </p>
            <Button onClick={generateDNAProfile} disabled={generating} size="lg" className="text-lg">
              {generating ? (
                <>
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  Generating Your DNA Profile...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generate My DNA Profile
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const allStrands = [
    ...(dnaProfile.technical_strands || []),
    ...(dnaProfile.soft_skill_strands || []),
    ...(dnaProfile.cognitive_strands || [])
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
                <Dna className="w-10 h-10 text-indigo-600" />
                Your Skill DNA
              </h1>
              <p className="text-gray-600">
                Deep multi-dimensional analysis of your career capabilities
              </p>
            </div>
            <Button onClick={generateDNAProfile} disabled={generating} variant="outline">
              <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
              Regenerate
            </Button>
          </div>
        </div>

        {/* DNA Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
            <CardContent className="pt-6">
              <div className="text-center">
                <Dna className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm opacity-90">DNA Strength</p>
                <p className="text-3xl font-bold">{dnaProfile.dna_strength_score}/100</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
            <CardContent className="pt-6">
              <div className="text-center">
                <Sparkles className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm opacity-90">Skill Genes</p>
                <p className="text-3xl font-bold">{dnaProfile.skill_genes?.length || 0}</p>
                <p className="text-xs opacity-80">Natural Strengths</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-500 to-orange-600 text-white">
            <CardContent className="pt-6">
              <div className="text-center">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm opacity-90">Mutations</p>
                <p className="text-3xl font-bold">{dnaProfile.skill_mutations?.length || 0}</p>
                <p className="text-xs opacity-80">Growth Areas</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white">
            <CardContent className="pt-6">
              <div className="text-center">
                <Target className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm opacity-90">Completion</p>
                <p className="text-3xl font-bold">{dnaProfile.completion_percentage}%</p>
                <p className="text-xs opacity-80">Profile Complete</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="helix">
              <Dna className="w-4 h-4 mr-2" />
              DNA Helix
            </TabsTrigger>
            <TabsTrigger value="genes">
              <Sparkles className="w-4 h-4 mr-2" />
              Genes
            </TabsTrigger>
            <TabsTrigger value="mutations">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Mutations
            </TabsTrigger>
            <TabsTrigger value="personality">
              <Heart className="w-4 h-4 mr-2" />
              Personality
            </TabsTrigger>
            <TabsTrigger value="career">
              <Briefcase className="w-4 h-4 mr-2" />
              Career Fit
            </TabsTrigger>
            <TabsTrigger value="learning">
              <Brain className="w-4 h-4 mr-2" />
              Learning
            </TabsTrigger>
          </TabsList>

          {/* DNA Helix Tab */}
          <TabsContent value="helix">
            <Card>
              <CardHeader>
                <CardTitle>DNA Helix Visualization</CardTitle>
                <CardDescription>
                  Your skills visualized as a genetic code. Each strand represents a different capability.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* DNA Helix Visualization */}
                <div className="relative h-[600px] bg-gradient-to-br from-indigo-900 to-purple-900 rounded-lg p-8 overflow-hidden">
                  {/* Helix Background Pattern */}
                  <div className="absolute inset-0 opacity-20">
                    <svg className="w-full h-full" viewBox="0 0 400 600">
                      <defs>
                        <linearGradient id="helixGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#3B82F6" />
                          <stop offset="50%" stopColor="#8B5CF6" />
                          <stop offset="100%" stopColor="#EC4899" />
                        </linearGradient>
                      </defs>
                      {/* Left strand */}
                      <path
                        d="M 100 0 Q 50 150, 100 300 Q 150 450, 100 600"
                        stroke="url(#helixGradient)"
                        strokeWidth="4"
                        fill="none"
                      />
                      {/* Right strand */}
                      <path
                        d="M 300 0 Q 350 150, 300 300 Q 250 450, 300 600"
                        stroke="url(#helixGradient)"
                        strokeWidth="4"
                        fill="none"
                      />
                      {/* Connecting bars */}
                      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                        <line
                          key={i}
                          x1={100 + Math.sin(i * 0.6) * 50}
                          y1={i * 60}
                          x2={300 - Math.sin(i * 0.6) * 50}
                          y2={i * 60}
                          stroke="url(#helixGradient)"
                          strokeWidth="2"
                          opacity="0.6"
                        />
                      ))}
                    </svg>
                  </div>

                  {/* Strand Cards */}
                  <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[550px] overflow-y-auto">
                    {allStrands.map((strand) => (
                      <Card
                        key={strand.id}
                        className={`border-2 ${
                          strand.is_gene
                            ? "border-green-400 bg-green-50"
                            : strand.is_mutation
                            ? "border-red-400 bg-red-50"
                            : "border-gray-200"
                        }`}
                      >
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <div
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: strand.color }}
                                />
                                <p className="font-semibold text-sm">{strand.name}</p>
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {strand.category.replace("_", " ")}
                              </Badge>
                            </div>
                            {strand.is_gene && (
                              <Sparkles className="w-5 h-5 text-green-600" />
                            )}
                            {strand.is_mutation && (
                              <AlertTriangle className="w-5 h-5 text-red-600" />
                            )}
                          </div>
                          <div className="mt-3">
                            <div className="flex justify-between text-xs mb-1">
                              <span>Strength</span>
                              <span className="font-semibold">{strand.strength}/100</span>
                            </div>
                            <Progress value={strand.strength} className="h-2" />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Legend */}
                <div className="mt-6 flex flex-wrap gap-4 justify-center">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Skill Gene (Natural Strength)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                    <span className="text-sm">Skill Mutation (Growth Area)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    <span className="text-sm">Technical</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-sm">Soft Skills</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-purple-500" />
                    <span className="text-sm">Cognitive</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Skill Genes Tab */}
          <TabsContent value="genes">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-green-600" />
                  Skill Genes - Your Natural Strengths
                </CardTitle>
                <CardDescription>
                  These are your genetic advantages - skills that come naturally to you
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dnaProfile.skill_genes?.length === 0 ? (
                  <div className="text-center py-12">
                    <Sparkles className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-600">
                      Complete more courses and assessments to discover your skill genes!
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {dnaProfile.skill_genes?.map((gene, index) => (
                      <Card key={index} className="border-2 border-green-400 bg-green-50">
                        <CardContent className="pt-6">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-full bg-green-500 text-white flex items-center justify-center flex-shrink-0">
                              <Sparkles className="w-6 h-6" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-lg font-bold text-green-900 mb-1">
                                {gene.skill_name}
                              </h3>
                              <div className="flex items-center gap-2 mb-3">
                                <Progress value={gene.strength} className="flex-1 h-2" />
                                <span className="text-sm font-semibold text-green-700">
                                  {gene.strength}/100
                                </span>
                              </div>
                              <p className="text-sm text-gray-700 mb-3">{gene.career_impact}</p>
                              <div className="bg-white rounded-lg p-3">
                                <p className="text-xs font-semibold text-gray-600 mb-2">Evidence:</p>
                                <ul className="space-y-1">
                                  {gene.evidence?.map((evidence, i) => (
                                    <li key={i} className="text-xs text-gray-600 flex items-start gap-2">
                                      <CheckCircle2 className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                                      {evidence}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Skill Mutations Tab */}
          <TabsContent value="mutations">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                  Skill Mutations - Growth Opportunities
                </CardTitle>
                <CardDescription>
                  Areas where focused development will yield significant career impact
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dnaProfile.skill_mutations?.length === 0 ? (
                  <div className="text-center py-12">
                    <Award className="w-16 h-16 mx-auto text-green-500 mb-4" />
                    <p className="text-gray-600">
                      Great! No critical skill gaps detected. Keep up the good work!
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {dnaProfile.skill_mutations?.map((mutation, index) => (
                      <Card key={index} className="border-2 border-red-400 bg-red-50">
                        <CardContent className="pt-6">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-full bg-red-500 text-white flex items-center justify-center flex-shrink-0">
                              <AlertTriangle className="w-6 h-6" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <h3 className="text-lg font-bold text-red-900">
                                  {mutation.skill_name}
                                </h3>
                                <Badge
                                  variant={mutation.priority === "high" ? "destructive" : "secondary"}
                                >
                                  {mutation.priority} priority
                                </Badge>
                              </div>
                              <div className="grid grid-cols-2 gap-4 mb-3">
                                <div>
                                  <p className="text-xs text-gray-600">Current Level</p>
                                  <p className="text-2xl font-bold text-red-700">
                                    {mutation.current_level}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-600">Target Level</p>
                                  <p className="text-2xl font-bold text-green-700">
                                    {mutation.target_level}
                                  </p>
                                </div>
                              </div>
                              <div className="bg-white rounded-lg p-3">
                                <p className="text-xs font-semibold text-gray-600 mb-2">
                                  Improvement Path:
                                </p>
                                <ul className="space-y-1">
                                  {mutation.improvement_path?.map((step, i) => (
                                    <li key={i} className="text-xs text-gray-600 flex items-start gap-2">
                                      <ChevronRight className="w-3 h-3 text-red-600 mt-0.5 flex-shrink-0" />
                                      {step}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Personality Tab */}
          <TabsContent value="personality">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="w-6 h-6 text-pink-600" />
                  Personality DNA
                </CardTitle>
                <CardDescription>
                  Your personality traits and their career implications
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dnaProfile.personality_traits ? (
                  <div className="space-y-6">
                    {/* Dominant Trait */}
                    <div className="bg-gradient-to-r from-pink-100 to-purple-100 p-6 rounded-lg">
                      <h3 className="text-lg font-bold mb-2">Dominant Trait</h3>
                      <p className="text-2xl font-bold text-purple-900 mb-2">
                        {dnaProfile.personality_traits.dominant_trait}
                      </p>
                      <p className="text-gray-700">
                        {dnaProfile.personality_traits.trait_description}
                      </p>
                    </div>

                    {/* Big Five */}
                    <div>
                      <h3 className="text-lg font-bold mb-4">Big Five Personality Traits</h3>
                      <div className="space-y-3">
                        {[
                          { name: "Openness", value: dnaProfile.personality_traits.openness },
                          { name: "Conscientiousness", value: dnaProfile.personality_traits.conscientiousness },
                          { name: "Extraversion", value: dnaProfile.personality_traits.extraversion },
                          { name: "Agreeableness", value: dnaProfile.personality_traits.agreeableness },
                          { name: "Emotional Stability", value: 100 - dnaProfile.personality_traits.neuroticism }
                        ].map((trait) => (
                          <div key={trait.name}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="font-medium">{trait.name}</span>
                              <span className={`font-semibold ${getScoreColor(trait.value)}`}>
                                {trait.value}/100
                              </span>
                            </div>
                            <Progress value={trait.value} className="h-2" />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Career Traits */}
                    <div>
                      <h3 className="text-lg font-bold mb-4">Career-Relevant Traits</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {[
                          { name: "Adaptability", value: dnaProfile.personality_traits.adaptability },
                          { name: "Leadership", value: dnaProfile.personality_traits.leadership },
                          { name: "Teamwork", value: dnaProfile.personality_traits.teamwork },
                          { name: "Resilience", value: dnaProfile.personality_traits.resilience },
                          { name: "Ambition", value: dnaProfile.personality_traits.ambition }
                        ].map((trait) => (
                          <Card key={trait.name}>
                            <CardContent className="pt-4">
                              <p className="text-xs text-gray-600 mb-1">{trait.name}</p>
                              <p className="text-2xl font-bold">{trait.value}</p>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Heart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-600 mb-4">
                      Complete personality assessment to unlock this section
                    </p>
                    <Button onClick={() => navigate("/assessments")}>
                      Take Personality Assessment
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Career Compatibility Tab */}
          <TabsContent value="career">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="w-6 h-6 text-indigo-600" />
                  Career Compatibility
                </CardTitle>
                <CardDescription>
                  Careers that match your DNA profile
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {dnaProfile.career_compatibilities?.map((career, index) => (
                    <Card key={index} className="border-2 border-indigo-200">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-bold mb-1">{career.career_title}</h3>
                            <Badge variant={career.overall_readiness >= 75 ? "default" : "secondary"}>
                              {career.time_to_ready}
                            </Badge>
                          </div>
                          <div className="text-right">
                            <p className="text-3xl font-bold text-indigo-600">
                              {career.compatibility_score}%
                            </p>
                            <p className="text-xs text-gray-600">Match</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div>
                            <p className="text-xs text-gray-600">Personality Fit</p>
                            <Progress value={career.personality_fit} className="h-2 mt-1" />
                            <p className="text-xs font-semibold mt-1">{career.personality_fit}%</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600">Learning Style</p>
                            <Progress value={career.learning_style_fit} className="h-2 mt-1" />
                            <p className="text-xs font-semibold mt-1">{career.learning_style_fit}%</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600">Work Preference</p>
                            <Progress value={career.work_preference_fit} className="h-2 mt-1" />
                            <p className="text-xs font-semibold mt-1">{career.work_preference_fit}%</p>
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="bg-green-50 p-3 rounded-lg">
                            <p className="text-xs font-semibold text-green-900 mb-2 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              Matching Strengths
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {career.matching_genes?.map((gene, i) => (
                                <Badge key={i} variant="outline" className="text-xs bg-green-100">
                                  {gene}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div className="bg-orange-50 p-3 rounded-lg">
                            <p className="text-xs font-semibold text-orange-900 mb-2 flex items-center gap-1">
                              <Target className="w-3 h-3" />
                              Skills to Develop
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {career.required_mutations?.map((mutation, i) => (
                                <Badge key={i} variant="outline" className="text-xs bg-orange-100">
                                  {mutation}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Learning Style Tab */}
          <TabsContent value="learning">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="w-6 h-6 text-purple-600" />
                  Learning DNA
                </CardTitle>
                <CardDescription>
                  How you learn best and cognitive profile
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dnaProfile.learning_style && dnaProfile.cognitive_profile ? (
                  <div className="space-y-6">
                    {/* Learning Style */}
                    <div>
                      <h3 className="text-lg font-bold mb-4">Learning Style Profile</h3>
                      <div className="grid md:grid-cols-2 gap-6">
                        <Card className="bg-purple-50">
                          <CardContent className="pt-6">
                            <p className="text-sm text-gray-600 mb-2">Primary Learning Style</p>
                            <p className="text-2xl font-bold text-purple-900 mb-4 capitalize">
                              {dnaProfile.learning_style.primary_style}
                            </p>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span>Pace Preference:</span>
                                <span className="font-semibold capitalize">
                                  {dnaProfile.learning_style.pace_preference}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>Environment:</span>
                                <span className="font-semibold capitalize">
                                  {dnaProfile.learning_style.environment_preference}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>Retention:</span>
                                <span className="font-semibold capitalize">
                                  {dnaProfile.learning_style.retention_method}
                                </span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        <Card className="bg-indigo-50">
                          <CardContent className="pt-6">
                            <p className="text-sm text-gray-600 mb-2">Learning Velocity</p>
                            <div className="mb-4">
                              <Progress
                                value={dnaProfile.learning_style.strength_score}
                                className="h-4"
                              />
                              <p className="text-2xl font-bold text-indigo-900 mt-2">
                                {dnaProfile.learning_style.strength_score}/100
                              </p>
                            </div>
                            <p className="text-xs text-gray-600">
                              Your learning efficiency compared to others
                            </p>
                          </CardContent>
                        </Card>
                      </div>
                    </div>

                    {/* Cognitive Profile */}
                    <div>
                      <h3 className="text-lg font-bold mb-4">Cognitive Abilities</h3>
                      <div className="space-y-3">
                        {[
                          { name: "Logical Reasoning", value: dnaProfile.cognitive_profile.logical_reasoning },
                          { name: "Verbal Comprehension", value: dnaProfile.cognitive_profile.verbal_comprehension },
                          { name: "Spatial Reasoning", value: dnaProfile.cognitive_profile.spatial_reasoning },
                          { name: "Numerical Ability", value: dnaProfile.cognitive_profile.numerical_ability },
                          { name: "Memory Retention", value: dnaProfile.cognitive_profile.memory_retention },
                          { name: "Processing Speed", value: dnaProfile.cognitive_profile.processing_speed },
                          { name: "Problem Solving", value: dnaProfile.cognitive_profile.problem_solving },
                          { name: "Creative Thinking", value: dnaProfile.cognitive_profile.creative_thinking }
                        ].map((ability) => (
                          <div key={ability.name}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="font-medium">{ability.name}</span>
                              <span className={`font-semibold ${getScoreColor(ability.value)}`}>
                                {ability.value}/100
                              </span>
                            </div>
                            <Progress value={ability.value} className="h-2" />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* IQ Estimate */}
                    <Card className="bg-gradient-to-r from-purple-100 to-pink-100">
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <p className="text-sm text-gray-600 mb-2">Estimated IQ Range</p>
                          <p className="text-4xl font-bold text-purple-900">
                            {dnaProfile.cognitive_profile.overall_iq_estimate}
                          </p>
                          <p className="text-xs text-gray-600 mt-2">
                            Based on cognitive ability assessment
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Brain className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-600 mb-4">
                      Complete cognitive and learning style assessments
                    </p>
                    <Button onClick={() => navigate("/assessments")}>
                      Take Assessments
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Footer />
    </div>
  );
};

export default SkillDNA;
